import React, { useState, useCallback, useRef } from 'react';
import jsPDF from 'jspdf';
import companyLogo from '../assets/company_logo.jpg';
import html2canvas from 'html2canvas';
import { sans, mono, green, greenLight, red, amber, useTheme, useCurrency } from '../tokens';
import { fmtCompactC } from '../helpers';
import { MONTHLY_ACTUALS } from '../data/monthlyActuals';
import { getCanonicalHeadcount, assertHeadcountConsistent } from '../data/canonicalHeadcount';
import KeyHighlights from './investor/KeyHighlights';
import EditableSpendCard from './investor/EditableSpendCard';
import NextRaiseSection from './investor/NextRaiseSection';
// CommercialPipeline and DocumentVault are rendered by InvestorPage, not here.

// ─── Financial Data: January 2026 ────────────────────────────────────────────
const JAN_2026 = {
  period: 'January 2026',
  periodShort: 'Jan 2026',
  balanceSheet: {
    assets: {
      current: [
        { label: 'Cash and cash equivalents', jan: 2890000, dec: 4620000, nov: 5700000 },
        { label: 'Short-term investments', jan: 32600000, dec: 32780000, nov: 33750000 },
        { label: 'Accounts receivable', jan: 1130, dec: 1410, nov: 0 },
        { label: 'Prepaid expenses & deposits', jan: 742000, dec: 599000, nov: 582000 },
        { label: 'Other receivables', jan: 257000, dec: 208000, nov: 428000 },
      ],
      totalCurrent: { jan: 36490130, dec: 38208410, nov: 40460000 },
      other: [
        { label: 'Interest receivable', jan: 912000, dec: 823000, nov: 964000 },
        { label: 'Government assistance receivable', jan: 1138000, dec: 1138000, nov: 1547000 },
        { label: 'Rental deposits', jan: 227000, dec: 227000, nov: 227000 },
        { label: 'Accrued lease inducements receivable', jan: 920000, dec: 920000, nov: 920000 },
      ],
      totalOther: { jan: 3197000, dec: 3108000, nov: 3658000 },
      propertyEquipment: { jan: 8450000, dec: 8499000, nov: 8544000 },
    },
    totalAssets: { jan: 48137130, dec: 49815410, nov: 52662000 },
    liabilities: {
      current: [
        { label: 'Accounts payable', jan: 496000, dec: 788000, nov: 739000 },
        { label: 'Accrued liabilities', jan: 0, dec: 183000, nov: 0 },
        { label: 'Other payables', jan: 490, dec: 118000, nov: 0 },
      ],
      totalCurrent: { jan: 496490, dec: 1089000, nov: 739000 },
      longTerm: [
        { label: 'Debt, long-term portion', jan: 144000, dec: 148000, nov: 152000 },
        { label: 'Deferred lease inducements', jan: 2704000, dec: 2717000, nov: 2720000 },
      ],
      totalLiabilities: { jan: 3344490, dec: 3954000, nov: 3611000 },
    },
    equity: {
      shareCapital: { jan: 77700000, dec: 77700000, nov: 77700000 },
      additionalPaidIn: { jan: 3930, dec: 3930, nov: 3930 },
      contributedSurplus: { jan: 347000, dec: 347000, nov: 347000 },
      deficit: { jan: -33297290, dec: -32227520, nov: -29040930 },
      totalEquity: { jan: 44792640, dec: 45861410, nov: 49051000 },
    },
  },
  profitLoss: {
    lines: [
      { label: 'Research & development', jan: 820000, fy25Avg: 391000, isExpense: true },
      { label: 'General & administrative', jan: 145000, fy25Avg: 200000, isExpense: true },
    ],
    operatingExpenses: { jan: 965000, fy25Avg: 591000 },
    otherIncome: [
      { label: 'Financial income', jan: 136000, fy25Avg: 147000, isIncome: true },
      { label: 'Foreign exchange gain (loss)', jan: -175000, fy25Avg: -186000 },
      { label: 'Government grants', jan: 11000, fy25Avg: 6900, isIncome: true },
    ],
    nonCash: [
      { label: 'Financial expenses', jan: 6160, fy25Avg: 3760 },
      { label: 'Amortization', jan: 70700, fy25Avg: 57000 },
    ],
    netLoss: { jan: -1069860, fy25Avg: -683860 },
  },
  cashFlow: {
    operating: -1703000,
    investing: 158000,
    financing: -5300,
    changeCash: -1730000,
    cashEnd: 2890000,
    cashBegin: 4620000,
    burnRate3mo: 1176000,
    totalCashInvestments: 35490000,
    runway: 33,
  },
  highlights: {
    rdSpend: { total: 472000, breakdown: 'Clinical trials ($178K), device manufacturing ($138K), testing & design ($102K), cleanroom & electrical ($54K)' },
    gaSpend: {
      total: 145000, // January 2026 (monthly) — must match the UI parent
      // Breakdown is monthly as well; values below must sum to <= total
      items: [
        { label: 'Software renewals', amount: 49000 },
        { label: 'Facilities & rent', amount: 40000 },
        { label: 'Patent & IP management', amount: 25000 },
        { label: 'Insurance & professional fees', amount: 18000 },
        { label: 'Other G&A', amount: 13000 },
      ],
    },
    newHires: 1,
  },
  milestones: [
    { period: 'Q1 2026', title: 'Feasibility Study — up to n=10', sub: 'Early Feasibility Study enrollment underway' },
    { period: 'Q2 2026', title: 'Design Freeze & V&V for EFS', sub: 'Design Master Record & Manufacturing complete' },
    { period: 'Q2–Q3 2026', title: 'FDA/HC Review for EFS', sub: 'Regulatory submission for CAN/USA Early Feasibility' },
    { period: 'Q3–Q4 2026', title: 'Feasibility CAN/USA — up to n=5', sub: 'Expanded clinical sites in Canada & United States' },
    { period: 'Mid-Q3 2027', title: 'US$75M Series B Close', sub: 'Funding Pivotal CAN/USA (n=300)' },
  ],
  // headcount is no longer stored here — canonical source is getCanonicalHeadcount()
};

// ─── Period Data Lookup ─────────────────────────────────────────────────────
// Build Dec 2025 and Nov 2025 from the existing multi-column data
function buildPeriodData(col, period, periodShort) {
  const src = JAN_2026;
  const prev = col === 'dec' ? 'nov' : 'dec'; // comparison column
  return {
    period, periodShort,
    balanceSheet: {
      assets: {
        current: src.balanceSheet.assets.current.map(r => ({ label: r.label, jan: r[col], dec: r[prev] })),
        totalCurrent: { jan: src.balanceSheet.assets.totalCurrent[col], dec: src.balanceSheet.assets.totalCurrent[prev] },
        other: src.balanceSheet.assets.other.map(r => ({ label: r.label, jan: r[col], dec: r[prev] })),
        totalOther: { jan: src.balanceSheet.assets.totalOther[col], dec: src.balanceSheet.assets.totalOther[prev] },
        propertyEquipment: { jan: src.balanceSheet.assets.propertyEquipment[col], dec: src.balanceSheet.assets.propertyEquipment[prev] },
      },
      totalAssets: { jan: src.balanceSheet.totalAssets[col], dec: src.balanceSheet.totalAssets[prev] },
      liabilities: {
        current: src.balanceSheet.liabilities.current.map(r => ({ label: r.label, jan: r[col], dec: r[prev] })),
        totalCurrent: { jan: src.balanceSheet.liabilities.totalCurrent[col], dec: src.balanceSheet.liabilities.totalCurrent[prev] },
        longTerm: src.balanceSheet.liabilities.longTerm.map(r => ({ label: r.label, jan: r[col], dec: r[prev] })),
        totalLiabilities: { jan: src.balanceSheet.liabilities.totalLiabilities[col], dec: src.balanceSheet.liabilities.totalLiabilities[prev] },
      },
      equity: {
        shareCapital: { jan: src.balanceSheet.equity.shareCapital[col], dec: src.balanceSheet.equity.shareCapital[prev] },
        additionalPaidIn: { jan: src.balanceSheet.equity.additionalPaidIn[col], dec: src.balanceSheet.equity.additionalPaidIn[prev] },
        contributedSurplus: { jan: src.balanceSheet.equity.contributedSurplus[col], dec: src.balanceSheet.equity.contributedSurplus[prev] },
        deficit: { jan: src.balanceSheet.equity.deficit[col], dec: src.balanceSheet.equity.deficit[prev] },
        totalEquity: { jan: src.balanceSheet.equity.totalEquity[col], dec: src.balanceSheet.equity.totalEquity[prev] },
      },
    },
    profitLoss: src.profitLoss, // P&L only has jan vs fy25Avg — reused across periods
    cashFlow: col === 'dec'
      ? { operating: -1718000, investing: -970000, financing: -3650, changeCash: -1076000, cashEnd: 4620000, cashBegin: 5700000, burnRate3mo: 1176000, totalCashInvestments: 37400000, runway: 34 }
      : { operating: -1916000, investing: -180000, financing: -3730, changeCash: -2100000, cashEnd: 5700000, cashBegin: 7800000, burnRate3mo: 1176000, totalCashInvestments: 39440000, runway: 36 },
    highlights: src.highlights,
    milestones: src.milestones,
    headcount: col === 'dec' ? 52 : 51,
  };
}

const PERIOD_DATA = {
  'jan-2026': JAN_2026,
  'dec-2025': buildPeriodData('dec', 'December 2025', 'Dec 2025'),
  'nov-2025': buildPeriodData('nov', 'November 2025', 'Nov 2025'),
};

// ─── Formatting helpers ──────────────────────────────────────────────────────
function fmtK(v) {
  if (v === 0 || v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${Math.round(v / 1000).toLocaleString()}K`;
  return `$${v.toLocaleString()}`;
}

function fmtFull(v) {
  if (v === 0) return '—';
  const neg = v < 0;
  const formatted = Math.abs(v).toLocaleString('en-US');
  return neg ? `(${formatted})` : formatted;
}

function delta(current, reference) {
  if (!reference || reference === 0) return null;
  const pct = ((current - reference) / Math.abs(reference)) * 100;
  return pct;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ReportHeader({ t, onShowMilestones, fx, onGeneratePDF, selectedPeriod, onPeriodChange, periodLabel }) {
  const { currency, fxRate } = fx;
  return (
    <div style={{
      background: `linear-gradient(135deg, ${green}, #148a63)`,
      borderRadius: 12,
      padding: '32px 36px',
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -30, right: -30,
        width: 200, height: 200, borderRadius: '50%',
        background: 'rgba(255,255,255,0.06)',
      }} />
      <div style={{
        position: 'absolute', bottom: -50, left: '40%',
        width: 300, height: 300, borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
      }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.8, fontFamily: sans, marginBottom: 4 }}>
              Monthly Investor Report
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, fontFamily: sans, marginBottom: 4 }}>
              Demo Medical Devices® Inc.
            </div>
            <div style={{ fontSize: 13, opacity: 0.75, fontFamily: sans }}>
              {periodLabel || JAN_2026.period} · CONFIDENTIAL
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', fontFamily: sans, marginTop: 2 }}>
              Data as of January 31, 2026
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={selectedPeriod} onChange={e => onPeriodChange(e.target.value)}
              style={{ fontFamily: mono, fontSize: 11, padding: '4px 8px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer' }}>
              <option value="jan-2026" style={{ color: '#000' }}>January 2026</option>
              <option value="dec-2025" style={{ color: '#000' }}>December 2025</option>
              <option value="nov-2025" style={{ color: '#000' }}>November 2025</option>
            </select>
            {/* Upcoming Milestones button */}
            <button
              onClick={onShowMilestones}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8,
                padding: '10px 18px',
                color: '#fff',
                fontFamily: sans,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <span style={{ fontSize: 14 }}>&#9776;</span>
              Upcoming Milestones
            </button>
            {/* Generate Newsletter button */}
            <button
              onClick={onGeneratePDF}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8,
                padding: '10px 18px',
                color: '#fff',
                fontFamily: sans,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <span style={{ fontSize: 14 }}>&#128196;</span>
              Generate Newsletter - PDF
            </button>
          </div>
        </div>
      </div>
      {/* KPI pills */}
      <div style={{ display: 'flex', gap: 16, marginTop: 24, position: 'relative', zIndex: 1 }}>
        {[
          { label: 'Cash & Investments', value: fmtCompactCx(35490000, currency, fxRate) },
          { label: 'Total Assets', value: fmtCompactCx(48137130, currency, fxRate) },
          { label: 'Runway Until', value: (() => {
            const now = new Date(2026, 0);
            const endDate = new Date(now.getFullYear(), now.getMonth() + Math.round(33.2));
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${months[endDate.getMonth()]} ${endDate.getFullYear()}`;
          })(), sub: '33.2 months remaining' },
          { label: 'Monthly Burn', value: fmtCompactCx(1176000, currency, fxRate) },
        ].map((kpi, i) => (
          <div key={i} style={{
            flex: 1,
            background: 'rgba(255,255,255,0.12)',
            borderRadius: 8,
            padding: '12px 14px',
            backdropFilter: 'blur(8px)',
          }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.7, fontFamily: sans, marginBottom: 4 }}>
              {kpi.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: mono }}>{kpi.value}</div>
            {kpi.sub && <div style={{ fontSize: 9, fontFamily: mono, opacity: 0.6, marginTop: 2 }}>{kpi.sub}</div>}
          </div>
        ))}
        {/* Headcount card */}
        <div style={{
          flex: 1.4,
          background: 'rgba(255,255,255,0.12)',
          borderRadius: 8,
          padding: '12px 14px',
          backdropFilter: 'blur(8px)',
        }}>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.7, fontFamily: sans, marginBottom: 4 }}>
            Headcount
          </div>
          {(() => { const hc = getCanonicalHeadcount(); assertHeadcountConsistent(hc.total, 'InvestorReporting header'); return (
          <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: mono }}>{hc.total}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <div style={{ fontSize: 9, fontFamily: sans, opacity: 0.8 }}>
              <span style={{ fontWeight: 700, fontFamily: mono, fontSize: 11 }}>{hc.men}</span> Men
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.3)' }} />
            <div style={{ fontSize: 9, fontFamily: sans, opacity: 0.8 }}>
              <span style={{ fontWeight: 700, fontFamily: mono, fontSize: 11 }}>{hc.women}</span> Women
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.3)' }} />
            <div style={{ fontSize: 9, fontFamily: sans, opacity: 0.8 }}>
              <span style={{ fontWeight: 700, fontFamily: mono, fontSize: 11 }}>{hc.femalePct}%</span> F
            </div>
          </div>
          </>
          );})()}
        </div>
      </div>
    </div>
  );
}

// ─── Milestones Modal ────────────────────────────────────────────────────────
function MilestonesModal({ t, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: t.bgCard || '#fff',
          borderRadius: 14,
          padding: '32px 36px',
          width: 480,
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.2s ease-out',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>
            Upcoming Milestones
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 20, color: t.textMuted, padding: '4px 8px',
              borderRadius: 6, lineHeight: 1,
            }}
            onMouseEnter={e => e.currentTarget.style.background = t.bgHover || '#f0f0f0'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            &times;
          </button>
        </div>
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{
            position: 'absolute', left: 8, top: 10, bottom: 10,
            width: 2, background: `linear-gradient(to bottom, ${green}, ${green}40)`,
          }} />
          {JAN_2026.milestones.map((ms, i) => (
            <div key={i} style={{ marginBottom: i < JAN_2026.milestones.length - 1 ? 24 : 0, position: 'relative' }}>
              <div style={{
                position: 'absolute', left: -24, top: 4,
                width: 12, height: 12, borderRadius: '50%',
                background: i === 0 ? green : '#fff',
                border: `2px solid ${green}`,
                boxShadow: i === 0 ? `0 0 0 4px ${green}30` : 'none',
              }} />
              <div style={{
                display: 'inline-block',
                fontSize: 10, fontWeight: 700, fontFamily: mono,
                color: green, background: `${green}15`,
                padding: '3px 10px', borderRadius: 4, marginBottom: 6,
              }}>{ms.period}</div>
              <div style={{ fontSize: 14, fontWeight: 600, fontFamily: sans, color: t.textPrimary, marginBottom: 3 }}>{ms.title}</div>
              <div style={{ fontSize: 12, fontFamily: sans, color: t.textMuted, lineHeight: 1.5 }}>{ms.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PLSection({ t, fx, data }) {
  const pl = (data || JAN_2026).profitLoss;
  const { currency, fxRate } = fx;
  const c = (v) => cx(v, currency, fxRate);

  const PLRow = ({ label, jan, fy25, bold, total, isLoss, indent }) => {
    const d = delta(jan, fy25);
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 80px',
        alignItems: 'center',
        padding: `${bold ? 8 : 5}px 8px`,
        borderBottom: total ? 'none' : `1px solid ${t.borderLight}`,
        background: total ? (t.bgHover || '#f8f9fa') : 'transparent',
        borderRadius: total ? 6 : 0,
        marginTop: total ? 4 : 0,
      }}>
        <div style={{
          fontSize: bold ? 12 : 11,
          fontWeight: bold ? 700 : 400,
          fontFamily: sans,
          color: t.textPrimary,
          paddingLeft: indent ? 16 : 0,
        }}>{label}</div>
        <div style={{
          fontSize: 11, fontFamily: mono, textAlign: 'right',
          fontWeight: bold ? 700 : 400,
          color: isLoss ? red : t.textPrimary,
        }}>{fmtFull(c(jan))}</div>
        <div style={{
          fontSize: 11, fontFamily: mono, textAlign: 'right',
          color: t.textMuted,
        }}>{fmtFull(c(fy25))}</div>
        <div style={{ textAlign: 'right' }}>
          {d !== null && (
            <span style={{
              fontSize: 9, fontFamily: mono, fontWeight: 600,
              color: isLoss ? (d > 0 ? red : green) : (d > 0 ? green : red),
              background: isLoss ? (d > 0 ? '#fdeaea' : '#e8f5e9') : (d > 0 ? '#e8f5e9' : '#fdeaea'),
              padding: '2px 6px', borderRadius: 4,
            }}>
              {d > 0 ? '+' : ''}{d.toFixed(0)}%
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '20px 24px',
      display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>Profit & Loss</div>
          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>All figures in {currency}</div>
        </div>
        <select style={{ fontFamily: sans, fontSize: 9, padding: '2px 6px', border: `1px solid ${t.border}`, borderRadius: 3, color: t.textMuted, background: t.bgCard || 'transparent' }}>
          <option>vs FY25 Avg/Mo</option>
          <option>vs Prior Month</option>
          <option>vs Prior Quarter</option>
          <option>vs Prior Year</option>
        </select>
      </div>

      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px',
        padding: '6px 8px', borderBottom: `2px solid ${t.border}`, marginBottom: 4,
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}></div>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>Jan 2026</div>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>FY25 Avg/Mo</div>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>Var %</div>
      </div>

      {/* Operating expenses */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 8px 4px', marginTop: 4 }}>Operating Expenses</div>
      {pl.lines.map((line, i) => (
        <PLRow key={i} label={line.label} jan={line.jan} fy25={line.fy25Avg} indent />
      ))}
      <PLRow label="Total operating expenses" jan={pl.operatingExpenses.jan} fy25={pl.operatingExpenses.fy25Avg} bold total />

      {/* Other income */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '12px 8px 4px' }}>Other Income / (Expenses)</div>
      {pl.otherIncome.map((line, i) => (
        <PLRow key={i} label={line.label} jan={line.jan} fy25={line.fy25Avg} indent />
      ))}

      {/* Non-cash */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '12px 8px 4px' }}>Non-Cash Items</div>
      {pl.nonCash.map((line, i) => (
        <PLRow key={i} label={line.label} jan={line.jan} fy25={line.fy25Avg} indent />
      ))}

      {/* Spacer to push net loss to bottom */}
      <div style={{ flex: 1 }} />

      {/* Net loss */}
      <div style={{ marginTop: 8, borderTop: `2px solid ${t.border}`, paddingTop: 4 }}>
        <PLRow label="Net Loss" jan={pl.netLoss.jan} fy25={pl.netLoss.fy25Avg} bold total isLoss />
      </div>
    </div>
  );
}

function BSSection({ t, fx, data }) {
  const bs = (data || JAN_2026).balanceSheet;
  const { currency, fxRate } = fx;
  const c = (v) => cx(v, currency, fxRate);

  const BSRow = ({ label, jan, dec, bold, total, indent }) => (
    <div style={{
      display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr',
      alignItems: 'center',
      padding: `${bold ? 7 : 4}px 8px`,
      borderBottom: total ? 'none' : `1px solid ${t.borderLight}`,
      background: total ? (t.bgHover || '#f8f9fa') : 'transparent',
      borderRadius: total ? 6 : 0,
      marginTop: total ? 2 : 0,
    }}>
      <div style={{
        fontSize: bold ? 11 : 10.5, fontWeight: bold ? 700 : 400,
        fontFamily: sans, color: t.textPrimary, paddingLeft: indent ? 14 : 0,
      }}>{label}</div>
      <div style={{ fontSize: 10.5, fontFamily: mono, textAlign: 'right', fontWeight: bold ? 700 : 400, color: t.textPrimary }}>{fmtFull(c(jan))}</div>
      <div style={{ fontSize: 10.5, fontFamily: mono, textAlign: 'right', fontWeight: bold ? 700 : 400, color: t.textMuted }}>{fmtFull(c(dec))}</div>
    </div>
  );

  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>Balance Sheet</div>
          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>All figures in {currency}</div>
        </div>
        <select style={{ fontFamily: sans, fontSize: 9, padding: '2px 6px', border: `1px solid ${t.border}`, borderRadius: 3, color: t.textMuted, background: t.bgCard || 'transparent' }}>
          <option>vs FY25 Avg/Mo</option>
          <option>vs Prior Month</option>
          <option>vs Prior Quarter</option>
          <option>vs Prior Year</option>
        </select>
      </div>

      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr', padding: '6px 8px', borderBottom: `2px solid ${t.border}`, marginBottom: 4 }}>
        <div />
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>Jan 31, 2026</div>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>Dec 31, 2025</div>
      </div>

      {/* Assets */}
      <div style={{ fontSize: 10, fontWeight: 700, fontFamily: sans, color: t.textPrimary, padding: '8px 8px 4px' }}>Assets</div>
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 4px' }}>Current</div>
      <BSRow label="Cash and cash equivalents" jan={bs.assets.current[0].jan} dec={bs.assets.current[0].dec} indent />
      <BSRow label="Short-term investments" jan={bs.assets.current[1].jan} dec={bs.assets.current[1].dec} indent />
      <BSRow label="Government assistance receivable" jan={bs.assets.other[1].jan} dec={bs.assets.other[1].dec} indent />
      <BSRow label="Other receivable" jan={bs.assets.current[2].jan + bs.assets.current[4].jan + bs.assets.other[0].jan} dec={bs.assets.current[2].dec + bs.assets.current[4].dec + bs.assets.other[0].dec} indent />
      <BSRow label="Sales tax receivable" jan={0} dec={0} indent />
      <BSRow label="Prepaid expenses" jan={bs.assets.current[3].jan} dec={bs.assets.current[3].dec} indent />
      <BSRow label="Total current assets" jan={bs.assets.totalCurrent.jan + bs.assets.other[0].jan + bs.assets.other[1].jan} dec={bs.assets.totalCurrent.dec + bs.assets.other[0].dec + bs.assets.other[1].dec} bold total />
      <BSRow label="Deposits" jan={bs.assets.other[2].jan} dec={bs.assets.other[2].dec} indent />
      <BSRow label="Property and equipment" jan={bs.assets.propertyEquipment.jan} dec={bs.assets.propertyEquipment.dec} indent />

      {/* Total Assets */}
      <div style={{ marginTop: 6, borderTop: `2px solid #718096`, paddingTop: 4 }}>
        <BSRow label="Total Assets" jan={bs.totalAssets.jan} dec={bs.totalAssets.dec} bold total />
      </div>

      {/* Liabilities and shareholders' equity */}
      <div style={{ fontSize: 10, fontWeight: 700, fontFamily: sans, color: t.textPrimary, padding: '12px 8px 4px' }}>Liabilities and shareholders' equity</div>
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: red, textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 4px' }}>Current</div>
      <BSRow label="Accounts payable and accrued liabilities" jan={bs.liabilities.current[0].jan + bs.liabilities.current[1].jan} dec={bs.liabilities.current[0].dec + bs.liabilities.current[1].dec} indent />
      <BSRow label="Current portion of long-term debt" jan={bs.liabilities.current[2].jan} dec={bs.liabilities.current[2].dec} indent />
      <BSRow label="Total current liabilities" jan={bs.liabilities.totalCurrent.jan} dec={bs.liabilities.totalCurrent.dec} bold total />
      <BSRow label="Long-term debt" jan={bs.liabilities.longTerm[0].jan} dec={bs.liabilities.longTerm[0].dec} indent />
      <BSRow label="Deferred lease inducements" jan={bs.liabilities.longTerm[1].jan} dec={bs.liabilities.longTerm[1].dec} indent />
      <BSRow label="Total liabilities" jan={bs.liabilities.totalLiabilities.jan} dec={bs.liabilities.totalLiabilities.dec} bold total />

      {/* Equity */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#5B7FD1', textTransform: 'uppercase', letterSpacing: 0.5, padding: '12px 8px 4px' }}>Shareholders' equity</div>
      <BSRow label="Share capital" jan={bs.equity.shareCapital.jan} dec={bs.equity.shareCapital.dec} indent />
      <BSRow label="Contributed surplus" jan={bs.equity.contributedSurplus.jan + bs.equity.additionalPaidIn.jan} dec={bs.equity.contributedSurplus.dec + bs.equity.additionalPaidIn.dec} indent />
      <BSRow label="Deficit" jan={bs.equity.deficit.jan} dec={bs.equity.deficit.dec} indent />
      <div style={{ marginTop: 4, borderTop: `2px solid #5B7FD1`, paddingTop: 4 }}>
        <BSRow label="Total shareholders' equity" jan={bs.equity.totalEquity.jan} dec={bs.equity.totalEquity.dec} bold total />
      </div>

      {/* Total Liabilities + Equity = Total Assets */}
      <div style={{ marginTop: 8, borderTop: `2px solid #718096`, paddingTop: 4 }}>
        <BSRow label="Total Liabilities and Shareholders' Equity" jan={bs.totalAssets.jan} dec={bs.totalAssets.dec} bold total />
      </div>
    </div>
  );
}

function CashFlowSection({ t, fx, data }) {
  const cf = (data || JAN_2026).cashFlow;
  const { currency, fxRate } = fx;
  const c = (v) => cx(v, currency, fxRate);
  const runwayDate = (() => {
    const now = new Date(2026, 0);
    const endDate = new Date(now.getFullYear(), now.getMonth() + Math.round(cf.runway));
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return { date: `${months[endDate.getMonth()]} ${endDate.getFullYear()}`, months: cf.runway };
  })();

  const CFRow = ({ label, value, bold, total, indent, section }) => (
    <div style={{
      display: 'grid', gridTemplateColumns: '2.5fr 1fr',
      alignItems: 'center',
      padding: `${bold ? 7 : 5}px 8px`,
      borderBottom: total ? 'none' : `1px solid ${t.borderLight}`,
      background: total ? (t.bgHover || '#f8f9fa') : 'transparent',
      borderRadius: total ? 6 : 0,
      marginTop: total ? 4 : 0,
    }}>
      <div style={{
        fontSize: bold ? 12 : 11, fontWeight: bold ? 700 : 400,
        fontFamily: sans, color: t.textPrimary, paddingLeft: indent ? 16 : 0,
      }}>{label}</div>
      <div style={{
        fontSize: 11, fontFamily: mono, fontWeight: bold ? 700 : 400,
        textAlign: 'right',
        color: value < 0 ? red : green,
      }}>{fmtFull(c(value))}</div>
    </div>
  );

  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '20px 24px',
      display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>Cash Flow Statement</div>
          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>All figures in {currency}</div>
        </div>
      </div>

      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2.5fr 1fr',
        padding: '6px 8px', borderBottom: `2px solid ${t.border}`, marginBottom: 4,
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}></div>
        <div style={{ fontSize: 9, fontWeight: 700, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' }}>Jan 2026</div>
      </div>

      {/* Cash Flow Activities */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 8px 4px', marginTop: 4 }}>Cash Flow Activities</div>
      <CFRow label="Operating activities" value={cf.operating} indent />
      <CFRow label="Investing activities" value={cf.investing} indent />
      <CFRow label="Financing activities" value={cf.financing} indent />
      <CFRow label="Change in cash during period" value={cf.changeCash} bold total />

      {/* Cash Position */}
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: sans, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, padding: '12px 8px 4px' }}>Cash Position</div>
      <CFRow label="Cash, beginning of period" value={cf.cashBegin} indent />
      <CFRow label="Cash, end of period" value={cf.cashEnd} indent />
      <CFRow label="Short-term investments" value={32600000} indent />
      <CFRow label="Total cash & investments" value={2890000 + 32600000} bold total />

    </div>
  );
}

function HighlightsSection({ t, type, fx, data }) {
  const { currency, fxRate } = fx;
  const fc = (v) => fmtCompactCx(v, currency, fxRate);
  if (type === 'rd') {
    return (
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>R&D Spend</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>{fc(820000)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {((data || JAN_2026).highlights.rdSpend.breakdown).split(', ').map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontFamily: sans, color: t.textSecondary }}>{item}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: sans, color: t.textMuted, marginBottom: 4 }}>
            <span>vs FY25 avg ({fc(391000)}/mo)</span>
            <span style={{ color: red, fontWeight: 700, fontSize: 12 }}>+109%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: t.borderLight, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #718096, #4a5568)' }} />
          </div>
        </div>
      </div>
    );
  }
  // G&A
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>G&A Spend</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>{fc(145000)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        {(() => {
          const ga = ((data || JAN_2026).highlights.gaSpend) || {};
          const items = ga.items || [];
          const parent = ga.total || 0;
          const sumChildren = items.reduce((s, it) => s + (it.amount || 0), 0);
          if (process.env.NODE_ENV !== 'production' && sumChildren > parent) {
            // eslint-disable-next-line no-console
            console.error(`[G&A] sum of sub-items (${sumChildren}) exceeds parent total (${parent}).`);
          }
          return items.map((it, i) => {
            const over = it.amount > parent;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ fontSize: 10, fontFamily: sans, color: t.textSecondary }}>{it.label}</span>
                <span style={{ fontSize: 10, fontFamily: mono, color: over ? red : t.textMuted }}>
                  ${Math.round(it.amount / 1000)}K{over ? ' ⚠' : ''}
                </span>
              </div>
            );
          });
        })()}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: sans, color: t.textMuted, marginBottom: 4 }}>
          <span>vs FY25 avg ({fc(200000)}/mo)</span>
          <span style={{ color: green, fontWeight: 700, fontSize: 12 }}>-28%</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: t.borderLight, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '72%', borderRadius: 3, background: 'linear-gradient(90deg, #a0aec0, #718096)' }} />
        </div>
      </div>
    </div>
  );
}

function MilestonesSection({ t }) {
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px' }}>
      <div style={{ fontSize: 14, fontWeight: 700, fontFamily: sans, color: t.textPrimary, marginBottom: 16 }}>
        Upcoming Milestones
      </div>
      <div style={{ position: 'relative', paddingLeft: 24 }}>
        {/* Timeline line */}
        <div style={{
          position: 'absolute', left: 7, top: 8, bottom: 8,
          width: 2, background: `linear-gradient(to bottom, #718096, #71809640)`,
        }} />
        {JAN_2026.milestones.map((ms, i) => (
          <div key={i} style={{ marginBottom: i < JAN_2026.milestones.length - 1 ? 20 : 0, position: 'relative' }}>
            {/* Dot */}
            <div style={{
              position: 'absolute', left: -20, top: 4,
              width: 12, height: 12, borderRadius: '50%',
              background: i === 0 ? '#718096' : '#fff',
              border: `2px solid #718096`,
              boxShadow: i === 0 ? `0 0 0 3px #71809630` : 'none',
            }} />
            <div style={{
              display: 'inline-block',
              fontSize: 9, fontWeight: 700, fontFamily: mono,
              color: '#4a5568', background: '#edf2f7',
              padding: '2px 8px', borderRadius: 4, marginBottom: 4,
            }}>{ms.period}</div>
            <div style={{ fontSize: 12, fontWeight: 600, fontFamily: sans, color: t.textPrimary, marginBottom: 2 }}>{ms.title}</div>
            <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted }}>{ms.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamSection({ t }) {
  const hc = getCanonicalHeadcount();
  assertHeadcountConsistent(hc.total, 'TeamSection');
  const history = hc.history || [];
  const trend = history.slice(-12);
  const growth = trend.length >= 2 ? trend[trend.length - 1].total - trend[0].total : 0;
  const deptEntries = Object.entries(hc.departments || {})
    .filter(([k]) => k !== 'Demo Medical')
    .sort((a, b) => b[1] - a[1]);
  const topDepts = deptEntries.slice(0, 6);
  const otherCount = deptEntries.slice(6).reduce((s, [, v]) => s + v, 0);
  const maxDept = topDepts.length ? topDepts[0][1] : 1;
  const maxTrend = trend.length ? Math.max(...trend.map(h => h.total)) : 1;
  const minTrend = trend.length ? Math.min(...trend.map(h => h.total)) : 0;
  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 12,
            background: `linear-gradient(135deg, ${green}, #148a63)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 24, fontWeight: 700, fontFamily: mono, color: '#fff' }}>{hc.total}</span>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>Employees</div>
            <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted, marginTop: 2 }}>
              {growth >= 0 ? '+' : ''}{growth} over last 12 months · {deptEntries.length} departments
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Men / Women</div>
            <div style={{ fontSize: 12, fontFamily: mono, color: t.textSecondary }}>{hc.men} / {hc.women}</div>
          </div>
        </div>
      </div>

      {/* 12-month trend */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 9, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
          Headcount Trend (12 mo)
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 40 }}>
          {trend.map((h, i) => {
            const hpct = maxTrend > 0 ? ((h.total - Math.max(0, minTrend - 2)) / (maxTrend - Math.max(0, minTrend - 2) || 1)) * 100 : 0;
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div title={`${h.month}: ${h.total}`} style={{
                  height: `${Math.max(6, hpct)}%`, background: '#3B82F6', borderRadius: 2, opacity: 0.85,
                }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Department breakdown */}
      <div>
        <div style={{ fontSize: 9, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
          By Department
        </div>
        {topDepts.map(([name, n]) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 10, fontFamily: sans, color: t.textSecondary, width: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ flex: 1, height: 8, background: t.borderLight, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${(n / maxDept) * 100}%`, height: '100%', background: '#3B82F6' }} />
            </div>
            <div style={{ fontSize: 10, fontFamily: mono, color: t.textPrimary, width: 22, textAlign: 'right' }}>{n}</div>
          </div>
        ))}
        {otherCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted, width: 160 }}>Other departments</div>
            <div style={{ flex: 1, height: 8, background: t.borderLight, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${(otherCount / maxDept) * 100}%`, height: '100%', background: t.textMuted }} />
            </div>
            <div style={{ fontSize: 10, fontFamily: mono, color: t.textPrimary, width: 22, textAlign: 'right' }}>{otherCount}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ask Section Component ───────────────────────────────────────────────────
function AskSection({ t }) {
  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '24px 32px',
    }}>
      <div style={{ fontSize: 13, fontFamily: sans, color: t.textPrimary, lineHeight: 1.7 }}>
        Demo Medical Devices® Inc. plans to raise its <span style={{ fontWeight: 700, color: green }}>US$75M Series B</span> in early 2027 to complete its Pivotal Study and obtain Pre-Market Approval (PMA).
      </div>
      <div style={{ fontSize: 12, fontFamily: sans, color: t.textMuted, lineHeight: 1.7, marginTop: 14 }}>
        As we begin preparing for this next stage, we would welcome any introductions or recommendations you may have for investors aligned with this phase of growth. We value building relationships well in advance and greatly appreciate your continued support.
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
// ─── PDF Newsletter Generator (reproduces exact newsletter format) ────────────
export function generateNewsletter(fx) {
  const { currency, fxRate } = fx;
  const c = (v) => currency === 'USD' ? Math.round(v / fxRate) : v;
  const fK = (v) => {
    const cv = c(v); const prefix = currency === 'USD' ? 'US$' : '$';
    const absK = Math.round(Math.abs(cv) / 1000);
    const formatted = absK.toLocaleString('en-US');
    return cv < 0 ? '(' + prefix + formatted + 'K)' : prefix + formatted + 'K';
  };
  const fKnoK = (v) => {
    const cv = c(v); const prefix = currency === 'USD' ? 'US$' : '$';
    const absK = Math.round(Math.abs(cv) / 1000);
    const formatted = absK.toLocaleString('en-US');
    return cv < 0 ? '(' + prefix + formatted + ')' : prefix + formatted;
  };
  const fM = (v) => {
    const cv = c(v); const prefix = currency === 'USD' ? 'US$' : '$';
    return prefix + (cv / 1e6).toFixed(1) + 'M';
  };

  const runwayDate = (() => {
    const now = new Date(2026, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + Math.round(33.2));
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return m[end.getMonth()] + ' ' + end.getFullYear();
  })();

  const lsText = (doc, text, x, y, opts) => {
    const spaced = text.split('').join('  ');
    doc.text(spaced, x, y, opts);
  };
  const lsTextCentered = (doc, text, y) => {
    const spaced = text.split('').join('  ');
    const tw = doc.getTextWidth(spaced);
    doc.text(spaced, (W - tw) / 2, y);
  };

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const mx = 23;
  const cW = W - mx * 2;
  const center = W / 2;

  const blue   = [74, 127, 192];
  const grn    = [29, 158, 117];
  const bgColor = [18, 18, 18];
  const blk    = [240, 240, 240]; // white text on dark bg
  const grayDk = [200, 200, 200];
  const grayMd = [160, 160, 160];
  const grayLt = [130, 130, 130];
  const lineClr = [60, 60, 60];

  // Helper to paint dark background on current page
  const paintBg = () => {
    doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    doc.rect(0, 0, W, H, 'F');
  };

  // PAGE 1
  paintBg();
  let y = 18;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blk[0], blk[1], blk[2]);
  doc.text('Demo Medical ', mx, y);
  const pmW = doc.getTextWidth('Demo Medical ');
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('Devices\u00ae', mx + pmW, y);
  const dW = doc.getTextWidth('Devices\u00ae');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blk[0], blk[1], blk[2]);
  doc.text(' Inc.', mx + pmW + dW, y);

  y += 18;
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blk[0], blk[1], blk[2]);
  doc.text('Demo Medical Devices', mx, y);

  y += 14;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text('January Financial Snapshot', mx, y);

  y += 28;
  doc.setDrawColor(lineClr[0], lineClr[1], lineClr[2]);
  doc.setLineWidth(0.3);
  doc.line(mx, y, W - mx, y);

  y += 5;
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsText(doc, 'STRICTLY CONFIDENTIAL \u2014 FOR PARTNERS AND INVESTORS OF DEMO MEDICAL', mx, y);
  y += 3;
  lsText(doc, 'DEVICES\u00ae INC.', mx, y);

  y += 16;
  const kpiBlockW = cW * 0.75;
  const kpiLeft = (W - kpiBlockW) / 2;
  const kpiSp = kpiBlockW / 3;

  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text(fM(35490000), kpiLeft, y);
  doc.text(fM(48137130), kpiLeft + kpiSp, y);
  doc.setTextColor(blue[0], blue[1], blue[2]);
  doc.text(runwayDate, kpiLeft + kpiSp * 2, y);

  y += 6;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('CASH & INVESTMENTS', kpiLeft, y);
  doc.text('TOTAL ASSETS', kpiLeft + kpiSp, y);
  doc.text('RUNWAY UNTIL', kpiLeft + kpiSp * 2, y);

  y += 4;
  doc.setFontSize(6);
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('33.2 months remaining', kpiLeft + kpiSp * 2, y);

  y += 22;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'PROFIT & LOSS \u2014 JANUARY 2026', y);

  y += 10;
  const colJan = mx + cW * 0.65;
  const colFy25 = mx + cW * 0.9;
  const tableW = cW;
  const rowH = 9;

  doc.setFillColor(40, 40, 40);
  doc.rect(mx, y - 4, tableW, 7, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text('JAN 2026', colJan, y, { align: 'right' });
  doc.text('FY25 AVG/MO', colFy25 + 12, y, { align: 'right' });

  y += 4;
  doc.setDrawColor(lineClr[0], lineClr[1], lineClr[2]);
  doc.setLineWidth(0.5);
  doc.line(mx, y, mx + tableW, y);

  var plData = [
    { label: 'Research & development', jan: 820000, fy25: 391000, indent: true },
    { label: 'General & administrative', jan: 145000, fy25: 200000, indent: true },
    { sep: true },
    { label: 'Operating expenses', jan: 965000, fy25: 591000, bold: true },
    { sep: true },
    { label: 'Financial income', jan: 136000, fy25: 147000, indent: true },
    { label: 'Foreign exchange gain (loss)', jan: -175000, fy25: -186000, indent: true },
    { label: 'Other items (net)', jan: -87000, fy25: -53600, indent: true },
    { sep: true },
    { label: 'Net loss', jan: -1069860, fy25: -683860, bold: true },
  ];

  plData.forEach(function(row) {
    if (row.sep) {
      doc.setDrawColor(224, 224, 224);
      doc.setLineWidth(0.3);
      doc.line(mx, y + 3, mx + tableW, y + 3);
      return;
    }
    y += rowH;
    doc.setFontSize(9);
    doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
    doc.setTextColor(row.bold ? blk[0] : grayDk[0], row.bold ? blk[1] : grayDk[1], row.bold ? blk[2] : grayDk[2]);
    doc.text(row.label, mx + (row.indent ? 6 : 0), y);
    doc.setTextColor(grayDk[0], grayDk[1], grayDk[2]);
    doc.text(fKnoK(row.jan), colJan, y, { align: 'right' });
    doc.text(fKnoK(row.fy25), colFy25 + 12, y, { align: 'right' });
  });

  y += 2;
  doc.setDrawColor(lineClr[0], lineClr[1], lineClr[2]);
  doc.setLineWidth(0.5);
  doc.line(mx, y, mx + tableW, y);

  // PAGE 2
  doc.addPage();
  paintBg();
  y = 15;

  doc.setFontSize(6);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('All figures in thousands ($000s). FY2025 monthly average = audited full-year actuals \u00f7 12.', center, y, { align: 'center' });

  y += 5;
  doc.setDrawColor(lineClr[0], lineClr[1], lineClr[2]);
  doc.setLineWidth(0.3);
  doc.line(mx, y, W - mx, y);

  y += 12;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'BURN RATE', y);

  y += 16;
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  var burnStr = fM(1176000);
  doc.text(burnStr, center, y, { align: 'center' });
  var burnW2 = doc.getTextWidth(burnStr);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('/month', center + burnW2 / 2 + 1, y);

  y += 7;
  doc.setFontSize(8);
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('Nov 2025 \u2013 Jan 2026', center, y, { align: 'center' });

  y += 38;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'UPCOMING MILESTONES', y);

  y += 12;
  var msX = mx + 8;
  JAN_2026.milestones.forEach(function(ms) {
    doc.setDrawColor(blue[0], blue[1], blue[2]);
    doc.setLineWidth(0.6);
    doc.rect(msX, y - 3.5, 4, 4);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(blue[0], blue[1], blue[2]);
    doc.text(ms.period.toUpperCase(), msX + 8, y);

    y += 5.5;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(blk[0], blk[1], blk[2]);
    doc.text(ms.title, msX + 8, y);

    y += 4.5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
    doc.text(ms.sub, msX + 8, y);

    y += 10;
  });

  y += 25;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'TEAM', y);

  y += 16;
  doc.setFontSize(48);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blue[0], blue[1], blue[2]);
  { const _hc = getCanonicalHeadcount(); doc.text(String(_hc.total), center, y, { align: 'center' }); }

  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'EMPLOYEES', y);

  y += 7;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  { const _hc = getCanonicalHeadcount();
    doc.text(`${_hc.men} Men  \u00b7  ${_hc.women} Women`, center, y, { align: 'center' }); }

  // PAGE 3
  doc.addPage();
  paintBg();
  y = 30;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  lsTextCentered(doc, 'HIGHLIGHTS', y);

  y += 30;
  var colWidth = 70;
  var gap = 20;
  var leftX = center - colWidth - gap / 2;
  var rightX = center + gap / 2;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text('R&D SPEND', leftX, y);
  doc.text('G&A SPEND', rightX, y);

  y += 10;
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text(fK(472000), leftX, y);
  doc.setTextColor(blue[0], blue[1], blue[2]);
  doc.text(fK(145000), rightX, y);

  y += 7;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  var rdBreak2 = 'Clinical trials ($178K), device manufacturing ($138K), testing & design ($102K), cleanroom & electrical ($54K)';
  var rdLines2 = doc.splitTextToSize(rdBreak2, colWidth);
  doc.text(rdLines2, leftX, y);
  var gaBreak2 = 'Software renewals ($49K), facilities & rent ($40K), patent & IP ($25K), insurance & prof. fees ($18K), other ($13K)';
  var gaLines2 = doc.splitTextToSize(gaBreak2, colWidth);
  doc.text(gaLines2, rightX, y);

  y += 30;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blue[0], blue[1], blue[2]);
  doc.text('Ask', mx, y);

  y += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayDk[0], grayDk[1], grayDk[2]);
  doc.text('Demo Medical Devices\u00ae Inc. plans to raise its ', mx, y);
  var a1w2 = doc.getTextWidth('Demo Medical Devices\u00ae Inc. plans to raise its ');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blue[0], blue[1], blue[2]);
  doc.text('US$75M Series B ', mx + a1w2, y);
  var a2w2 = doc.getTextWidth('US$75M Series B ');
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayDk[0], grayDk[1], grayDk[2]);
  doc.text('in early 2027 to complete its Pivotal', mx + a1w2 + a2w2, y);
  y += 6;
  doc.text('Study and obtain Pre-Market Approval (PMA).', mx, y);

  y += 12;
  doc.setFontSize(10);
  doc.setTextColor(grayDk[0], grayDk[1], grayDk[2]);
  var ask3t = 'As we begin preparing for this next stage, we would welcome any introductions or recommendations you may have for investors aligned with this phase of growth. We value building relationships well in advance and greatly appreciate your continued support.';
  doc.text(ask3t, mx, y, { maxWidth: cW, align: 'justify', lineHeightFactor: 1.8 });
  var ask3Lines2 = doc.splitTextToSize(ask3t, cW);

  y += ask3Lines2.length * 6 + 16;
  var footerY = H - 28;

  // Logo centered between Ask text and Thank you
  var logoSize = 25;
  doc.addImage(companyLogo, 'JPEG', center - logoSize / 2, footerY - 55, logoSize, logoSize);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(grayDk[0], grayDk[1], grayDk[2]);
  doc.text('Thank you for being a part of our team.', center, footerY - 18, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bolditalic');
  doc.setTextColor(grayMd[0], grayMd[1], grayMd[2]);
  doc.text('Building the safest solution to heart failure.', center, footerY - 10, { align: 'center' });
  doc.setDrawColor(lineClr[0], lineClr[1], lineClr[2]);
  doc.setLineWidth(0.7);
  doc.line(mx, footerY, W - mx, footerY);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  var fpmW = doc.getTextWidth('Demo Medical ');
  doc.setFont('helvetica', 'normal');
  var fdW2 = doc.getTextWidth('Devices\u00ae');
  doc.setFont('helvetica', 'bold');
  var fiW = doc.getTextWidth(' Inc.');
  var totalFooterW = fpmW + fdW2 + fiW;
  var footerLogoX = center - totalFooterW / 2;

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blk[0], blk[1], blk[2]);
  doc.text('Demo Medical ', footerLogoX, footerY + 10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(blue[0], blue[1], blue[2]);
  doc.text('Devices\u00ae', footerLogoX + fpmW, footerY + 10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(blk[0], blk[1], blk[2]);
  doc.text(' Inc.', footerLogoX + fpmW + fdW2, footerY + 10);

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(grayLt[0], grayLt[1], grayLt[2]);
  doc.text('Confidential. Do not reproduce or distribute without permission. \u00b7 alex@example.com', center, footerY + 15, { align: 'center' });

  doc.save('DemoMed_Newsletter_January_2026.pdf');
}

// Currency conversion helper: data is in CAD, convert to USD by dividing by fxRate
function cx(value, currency, fxRate) {
  if (currency === 'CAD' || !fxRate || fxRate === 1) return value;
  return Math.round(value / fxRate);
}

function fmtCompactCx(value, currency, fxRate) {
  const converted = cx(value, currency, fxRate);
  const prefix = currency === 'USD' ? 'US$' : '$';
  const abs = Math.abs(converted);
  if (abs >= 1e6) return `${prefix}${(converted / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${prefix}${(converted / 1e3).toFixed(0)}K`;
  return `${prefix}${converted}`;
}

export default function InvestorReporting({ selectedPeriod: propPeriod, companyId, readOnly = false }) {
  const t = useTheme();
  const { currency, fxRate } = useCurrency();
  const [showMilestones, setShowMilestones] = useState(false);
  const [localPeriod, setLocalPeriod] = useState('jan-2026');
  const selectedPeriod = propPeriod || localPeriod;
  const setSelectedPeriod = setLocalPeriod;
  const data = PERIOD_DATA[selectedPeriod] || JAN_2026;
  const fx = { currency, fxRate };
  const reportRef = useRef(null);

  return (
    <div ref={reportRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      {/* The Monthly Investor Report banner was removed — the KPI cards
          at the top of the Investor Dashboard already surface this info. */}

      {/* R&D and G&A side by side — fully admin-editable per period */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <EditableSpendCard companyId={companyId} selectedPeriod={selectedPeriod} type="rd" readOnly={readOnly} />
        <EditableSpendCard companyId={companyId} selectedPeriod={selectedPeriod} type="ga" readOnly={readOnly} />
      </div>

      {/* Key Highlights — fully admin-editable, per-period */}
      <KeyHighlights
        companyId={companyId}
        selectedPeriod={selectedPeriod}
        readOnly={readOnly}
        defaults={[
          `R&D spend increased 109% vs FY25 average ($${Math.round(data.profitLoss.lines[0].jan/1000)}K vs $${Math.round(data.profitLoss.lines[0].fy25Avg/1000)}K/mo), driven by clinical trial ramp-up and device manufacturing scale.`,
          `G&A spend decreased 28% vs FY25 average ($${Math.round(data.profitLoss.lines[1].jan/1000)}K vs $${Math.round(data.profitLoss.lines[1].fy25Avg/1000)}K/mo), reflecting cost discipline on non-core spending.`,
          `Net loss of $${Math.round(Math.abs(data.profitLoss.netLoss.jan)/1000)}K for the month, in line with accelerated development timeline.`,
          `Cash position remains strong at $${Math.round(data.cashFlow.totalCashInvestments/1000000)}M with approximately ${data.cashFlow.runway} months runway.`,
        ]}
      />

      {/* P&L, Cash Flow and Balance Sheet are no longer shown in the
          investor view — investors receive those in the monthly newsletter. */}

      {/* Next Raise removed — overkill for the investor monthly view for now. */}
      {/* Team / Employees block removed — the same info is surfaced by the
          expanded Headcount card at the top of the Investor Dashboard. */}

      {/* Footer removed — the investor page footer already carries this notice. */}

      {/* Milestones Modal */}
      {showMilestones && <MilestonesModal t={t} onClose={() => setShowMilestones(false)} />}
    </div>
  );
}
